import React, { useState } from 'react';
import {
  Layers,
  Database,
  Receipt,
  CheckSquare,
  AlertCircle,
  Printer,
  Mail,
  MessageCircle,
  Download
} from 'lucide-react';

interface InvoicePayload {
  invoice_id: string;
  client: string;
  tin: string;
  branch_code: string;
  address: string;
  gross: number;
  net: number;
  vat: number;
  vatable_sales: number;
  vat_exempt: number;
  zero_rated: number;
  tax_type: string;
  timestamp: string;
  items: InvoiceItem[];
}

export interface InvoiceItem {
  id: string;
  quantity: number;
  unit: string;
  description: string;
  unitPrice: number;
  taxClassification: 'VATable' | 'VAT-Exempt' | 'Zero-Rated' | 'Senior-Citizen';
}

interface EncryptedPayload {
  algorithm: string;
  iv: string;
  key_fingerprint: string;
  ciphertext: string;
  block_hash: string;
}

interface InvoiceRow {
  id: string;
  client: string;
  gross: number;
  plain_payload: InvoicePayload;
  encrypted_payload: EncryptedPayload;
}

interface Toast {
  id: string;
  title: string;
  body: string;
  color: 'emerald' | 'red' | 'orange' | 'blue';
}

const generatedCryptKey = "3bf88aefc5040e53a7b51b3a886f4a867cce6de37397c02b37ba4ef5752b1b3a";

export function SelyoWorkspace() {
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [ledger, setLedger] = useState<InvoiceRow[]>([]);
  const [syncQueue, setSyncQueue] = useState<InvoiceRow[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [tamperLock] = useState<boolean>(false);

  // Delivery Method Routing
  const [deliveryMethod, setDeliveryMethod] = useState<'print' | 'email' | 'messenger'>('print');

  // Form states
  const [custName, setCustName] = useState<string>('');
  const [custTin, setCustTin] = useState<string>('');
  const [branchCode, setBranchCode] = useState<string>('00000');
  const [custAddress, setCustAddress] = useState<string>('');
  const [custOscaPwd, setCustOscaPwd] = useState<string>('');
  const [items, setItems] = useState<InvoiceItem[]>([{ id: '1', quantity: 1, unit: 'Pcs', description: '', unitPrice: 0, taxClassification: 'VATable' }]);

  const licenseExpiry = Number(localStorage.getItem('selyo_license_expiry') || 0);
  const isLicenseActive = localStorage.getItem('selyo_license_active') === 'true';
  const isNearRenewal = isLicenseActive && licenseExpiry > 0 && (licenseExpiry - Date.now() > 0) && (licenseExpiry - Date.now() < 30 * 24 * 60 * 60 * 1000);

  const showToast = (title: string, body: string, color: 'emerald' | 'red' | 'orange' | 'blue') => {
    const toastId = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, { id: toastId, title, body, color }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== toastId));
    }, 5000);
  };

  let totalGross = 0;
  let totalNetOfVat = 0;
  let totalOutputVat = 0;
  let totalVatableSales = 0;
  let totalVatExempt = 0;
  let totalZeroRated = 0;
  let finalLedgerDeposit = 0;
  let hasSeniorCitizen = false;

  items.forEach(item => {
    const rowGross = (item.quantity || 0) * (item.unitPrice || 0);
    totalGross += rowGross;

    if (item.taxClassification === 'VATable') {
      const net = rowGross / 1.12;
      totalVatableSales += net;
      totalNetOfVat += net;
      totalOutputVat += rowGross - net;
      finalLedgerDeposit += rowGross;
    } else if (item.taxClassification === 'VAT-Exempt') {
      totalVatExempt += rowGross;
      totalNetOfVat += rowGross;
      finalLedgerDeposit += rowGross;
    } else if (item.taxClassification === 'Zero-Rated') {
      totalZeroRated += rowGross;
      totalNetOfVat += rowGross;
      finalLedgerDeposit += rowGross;
    } else if (item.taxClassification === 'Senior-Citizen') {
      hasSeniorCitizen = true;
      const net = rowGross / 1.12;
      const discount = net * 0.20;
      totalNetOfVat += net;
      finalLedgerDeposit += (net - discount);
      totalVatExempt += (net - discount); 
    }
  });

  const finalCalculatedTotal = finalLedgerDeposit;

  const toggleNetworkStatus = () => {
    const nextOnline = !isOnline;
    setIsOnline(nextOnline);
    if (nextOnline && syncQueue.length > 0) {
      showToast("Network Restored", `Synchronizing ${syncQueue.length} pending items to gateway.`, "blue");
      setSyncQueue([]);
    } else if (!nextOnline) {
      showToast("Offline Mode", "Network disconnected. Queuing dispatches locally.", "orange");
    }
  };

  const handleInvoiceSubmission = async (e: React.FormEvent) => {
    e.preventDefault();
    if (tamperLock) {
      showToast("SECURITY LOCK", "System frozen due to cryptographic ledger mismatch.", "red");
      return;
    }
    if (totalGross <= 0) {
      showToast("Error", "Invoice values must be larger than ₱0.00.", "red");
      return;
    }

    const invoiceId = `SLYO-2026-${String(ledger.length + 1).padStart(4, '0')}`;
    const finalClientString = hasSeniorCitizen && custOscaPwd ? `${custName} (OSCA ID: ${custOscaPwd})` : custName;
    const taxTypeStatus = items.length === 1 && items[0] ? items[0].taxClassification : 'Mixed Classification';

    const payload: InvoicePayload = {
      invoice_id: invoiceId,
      client: finalClientString,
      tin: custTin,
      branch_code: branchCode,
      address: custAddress,
      gross: finalCalculatedTotal,
      net: totalNetOfVat,
      vat: totalOutputVat,
      vatable_sales: totalVatableSales,
      vat_exempt: totalVatExempt,
      zero_rated: totalZeroRated,
      tax_type: taxTypeStatus,
      timestamp: new Date().toISOString(),
      items: items
    };

    const iv = Array.from({ length: 12 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    const mockCiphertext = btoa(JSON.stringify(payload)).substring(0, 48) + "...[AES-256-GCM-CIPHER]";

    // Blockchain Hash Linkage
    const previousBlockHash = (ledger.length > 0 && ledger[0]?.encrypted_payload) 
      ? ledger[0].encrypted_payload.block_hash 
      : '0000000000000000000000000000000000000000000000000000000000000000';
    const encoder = new TextEncoder();
    const dataToHash = encoder.encode(JSON.stringify(payload) + previousBlockHash);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataToHash);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const currentBlockHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    const localDbRow: InvoiceRow = {
      id: invoiceId,
      client: payload.client,
      gross: payload.gross,
      plain_payload: payload,
      encrypted_payload: {
        algorithm: "AES-GCM",
        iv: iv,
        key_fingerprint: "SHA-256:" + generatedCryptKey.substring(0, 10),
        ciphertext: `aes-gcm-0x${iv}:${mockCiphertext}`,
        block_hash: currentBlockHash
      }
    };

    setLedger(prev => [localDbRow, ...prev]);

    if (!isOnline) {
      setSyncQueue(prev => [...prev, localDbRow]);
      showToast("Pending Network Recovery...", "Saved locally in cryptographic queue.", "orange");
    } else {
      showToast("Invoice Synced", `${invoiceId} securely written and synced.`, "emerald");
    }

    // Process Delivery Route
    if (deliveryMethod === 'print') {
      setTimeout(() => window.print(), 500);
    } else if (deliveryMethod === 'email') {
      showToast("Email Dispatched", `Secure PDF receipt envelope queued for ${custName}.`, "blue");
    } else if (deliveryMethod === 'messenger') {
      showToast("Messenger Ping", "Receipt payload formatted for Messenger webhook.", "blue");
    }

    setCustName('');
    setCustTin('');
    setBranchCode('00000');
    setCustAddress('');
    setCustOscaPwd('');
    setItems([{ id: Date.now().toString(), quantity: 1, unit: 'Pcs', description: '', unitPrice: 0, taxClassification: 'VATable' }]);
  };

  const handleCsvExport = () => {
    if (ledger.length === 0) {
      showToast("Export Failed", "No records available to export.", "red");
      return;
    }

    const sysHeaders = [
      "SELYO FINTECH PLATFORM CORE ENGINE (cruzfind.com)",
      "DEVELOPER: CruzFind Technologies, Inc. | VERSION: 2.0.0",
      "REPORT TYPE: ELECTRONIC LOOSE-LEAF SALES JOURNAL",
      "BIR PERMIT TO USE (PTU) / ACKNOWLEDGEMENT NO: [PLACEHOLDER - PENDING AUDIT]",
      ""
    ].join("\n");

    const colHeaders = "Date,Invoice ID,Customer Name,Customer TIN,Customer Address,Gross Sales,VATable Sales,Output VAT (12%),VAT-Exempt Sales,Zero-Rated Sales,Verification Timestamp,Cryptographic Block Hash\n";
    
    const csvContent = ledger.map(r => {
      const p = r.plain_payload;
      
      let vatableSales = p.vatable_sales || 0;
      let outputVat = p.vat || 0;
      let vatExemptSales = p.vat_exempt || 0;
      let zeroRatedSales = p.zero_rated || 0;

      if (!p.items) {
        const taxType = p.tax_type;
        if (taxType === 'VATable' || taxType === 'VATable Transaction') {
          vatableSales = p.gross / 1.12;
          outputVat = (p.gross / 1.12) * 0.12;
        } else if (taxType === 'Exempt' || taxType === 'VAT-Exempt' || taxType === 'VAT-Exempt: Senior Citizen / PWD') {
          vatExemptSales = p.gross;
        } else if (taxType === 'Zero-Rated') {
          zeroRatedSales = p.gross;
        } else {
          vatableSales = p.net;
          outputVat = p.vat;
        }
      }

      const formattedDate = new Date(p.timestamp).toLocaleDateString();
      const escapedName = `"${(p.client || "").replace(/"/g, '""')}"`;
      const escapedAddress = `"${(p.address || "").replace(/"/g, '""')}"`;

      return `${formattedDate},${p.invoice_id},${escapedName},${p.tin},${escapedAddress},${p.gross.toFixed(2)},${vatableSales.toFixed(2)},${outputVat.toFixed(2)},${vatExemptSales.toFixed(2)},${zeroRatedSales.toFixed(2)},${p.timestamp},${r.encrypted_payload.block_hash}`;
    }).join("\n");
    
    const blob = new Blob([sysHeaders + "\n" + colHeaders + csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selyo_bir_loose_leaf_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Exported", "BIR Loose-Leaf Sales Journal generated successfully.", "emerald");
  };

  return (
    <div className="bg-[#F8F9FA] text-slate-800 min-h-screen flex flex-col font-sans selection:bg-[#0070BA] selection:text-white print:bg-white print:m-0 print:p-0">
      
      {tamperLock && (
        <div className="fixed inset-0 z-[9999] bg-[#D22630]/90 backdrop-blur-sm flex items-center justify-center print:hidden">
          <div className="bg-white p-8 rounded-3xl max-w-lg text-center shadow-2xl">
            <AlertCircle className="w-16 h-16 text-[#D22630] mx-auto mb-4" />
            <h2 className="text-2xl font-black text-slate-900 mb-2 uppercase">Security Tamper Block</h2>
            <p className="text-sm font-semibold text-slate-600">
              A cryptographic checksum mismatch was detected in the local block progression. The workspace has been frozen to preserve chain-of-custody integrity.
            </p>
          </div>
        </div>
      )}

      <div className="w-full max-w-7xl mx-auto px-6 py-4 flex justify-between items-center border-b border-zinc-100 transition-all duration-500 ease-in-out print:hidden">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-[#003087] tracking-tight">CruzFind</span>
          <span className="text-xs bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-mono font-medium">Selyo PWA v2.0</span>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Explicit Escape Route Link for Clients and Merchants */}
          <button
            type="button"
            onClick={() => {
              window.location.hash = '';
              window.location.pathname = '/'; // Routes directly back to public marketing homepage
            }}
            className="text-zinc-500 hover:text-[#0070BA] text-sm font-medium transition-all duration-300 ease-in-out flex items-center gap-1 cursor-pointer bg-transparent border-none"
          >
            ← Back to Home
          </button>
          
          <span className="text-zinc-200">|</span>
          
          {/* Destructive Session Log Out Action Flag */}
          <button
            type="button"
            onClick={() => {
              // Clear session storage parameters here
              localStorage.removeItem('selyo_user_role');
              localStorage.removeItem('selyo_user_email');
              localStorage.removeItem('cf_auth_token_signature');
              window.location.hash = '#admin'; // Push back to core lock gate system
            }}
            className="bg-transparent hover:bg-red-50 text-xs font-bold text-[#D22630] uppercase tracking-wider px-3 py-1.5 rounded-xl border border-transparent hover:border-red-200 transition-all duration-300 ease-in-out cursor-pointer"
          >
            🛑 Log Out Session
          </button>
        </div>
      </div>

      <header className="bg-[#003087] text-white sticky top-0 z-50 rounded-b-3xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] print:hidden transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shadow-inner border border-white/20">
              <Layers className="text-white w-7 h-7" />
            </div>
            <div>
              <span className="text-xl font-black tracking-tight text-white flex items-center gap-1.5">
                Selyo Corporate
              </span>
              <p className="text-[10px] text-white/80 tracking-widest uppercase font-bold">cruzfind.com fintech</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center bg-white/10 px-3 py-1.5 rounded-2xl border border-white/15">
              <span className="text-xs text-white/90 mr-3 hidden sm:inline font-bold uppercase tracking-wider">Gateway:</span>
              <button
                onClick={toggleNetworkStatus}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-xl transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-sm ${
                  isOnline 
                    ? 'bg-[#0070BA] text-white hover:bg-[#005ea6]' 
                    : 'bg-[#D22630] text-white hover:bg-[#b02028]'
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-white animate-pulse' : 'bg-white'}`} />
                <span className="text-xs font-bold uppercase tracking-widest">
                  {isOnline ? 'Active' : 'Offline'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {isNearRenewal && (
        <div className="bg-amber-50 border-y border-amber-200 py-3 px-4 text-center text-xs font-bold text-amber-800 flex items-center justify-center gap-2 rounded-2xl mx-auto max-w-5xl mt-4 shadow-sm print:hidden">
          <AlertCircle className="w-4.5 h-4.5 text-amber-600 shrink-0" />
          <span>Notice: Selyo corporate license is near expiry. Please consult your administrator.</span>
        </div>
      )}

      <main className="flex-grow max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8 w-full flex flex-col gap-8 print:w-full print:max-w-full print:p-0">
        
        {/* INVOICING CORE */}
        <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.06)] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] border border-slate-100 print:shadow-none print:border-none print:p-2">
          <div className="flex items-center justify-between mb-8 print:mb-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-2xl bg-[#0070BA]/10 flex items-center justify-center text-[#0070BA] border border-[#0070BA]/20 print:hidden">
                <Receipt className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-black text-[#003087] tracking-tight print:text-black">Principal Sales Invoice (EOPT)</h3>
                <p className="text-[10px] text-[#0070BA] font-bold uppercase tracking-widest print:text-black">RA 11976 Accrual-Basis Tax Engine</p>
              </div>
            </div>
            <div className="flex items-center bg-[#008751]/10 text-[#008751] px-3 py-1.5 rounded-xl text-xs font-bold print:hidden">
              12% VAT Applied
            </div>
          </div>

          <form onSubmit={handleInvoiceSubmission} className="space-y-6">
            <div className="space-y-6">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 print:text-black">Business Name / Customer Name</label>
                <input
                  type="text"
                  required
                  value={custName}
                  onChange={(e) => setCustName(e.target.value)}
                  placeholder="e.g. Cruz Mercantile Corp."
                  className="w-full bg-[#F8F9FA] border border-slate-200 rounded-2xl px-4 py-3.5 text-sm font-semibold text-[#003087] focus:outline-none focus:border-[#0070BA] focus:ring-2 focus:ring-[#0070BA]/20 transition-all duration-500 print:bg-white print:border-none print:p-0 print:text-black"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:gap-2">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 print:text-black">Customer TIN</label>
                  <input
                    type="text"
                    required
                    value={custTin}
                    onChange={(e) => setCustTin(e.target.value)}
                    placeholder="000-123-456"
                    className="w-full bg-[#F8F9FA] border border-slate-200 rounded-2xl px-4 py-3.5 text-sm font-mono font-semibold text-[#003087] focus:outline-none focus:border-[#0070BA] focus:ring-2 focus:ring-[#0070BA]/20 transition-all duration-500 print:bg-white print:border-none print:p-0 print:text-black"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 print:text-black">Branch Code</label>
                  <input
                    type="text"
                    required
                    value={branchCode}
                    onChange={(e) => setBranchCode(e.target.value)}
                    placeholder="00000"
                    className="w-full bg-[#F8F9FA] border border-slate-200 rounded-2xl px-4 py-3.5 text-sm font-mono font-semibold text-[#003087] focus:outline-none focus:border-[#0070BA] focus:ring-2 focus:ring-[#0070BA]/20 transition-all duration-500 print:bg-white print:border-none print:p-0 print:text-black"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 print:text-black">Registered Business Address</label>
                <textarea
                  required
                  rows={2}
                  value={custAddress}
                  onChange={(e) => setCustAddress(e.target.value)}
                  placeholder="Full physical address for BIR EOPT loose-leaf registration"
                  className="w-full bg-[#F8F9FA] border border-slate-200 rounded-2xl px-4 py-3.5 text-sm font-semibold text-[#003087] focus:outline-none focus:border-[#0070BA] focus:ring-2 focus:ring-[#0070BA]/20 transition-all duration-500 print:bg-white print:border-none print:p-0 print:text-black"
                />
              </div>

              {items.some(i => i.taxClassification === 'Senior-Citizen') && (
                <div className="print:hidden">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Senior Citizen / OSCA / PWD ID Number</label>
                  <input
                    type="text"
                    required
                    value={custOscaPwd}
                    onChange={(e) => setCustOscaPwd(e.target.value)}
                    placeholder="Senior Citizen / OSCA / PWD ID Number"
                    className="w-full bg-[#F8F9FA] border border-slate-200 rounded-2xl px-4 py-3.5 text-sm font-semibold text-[#003087] focus:outline-none focus:border-[#0070BA] focus:ring-2 focus:ring-[#0070BA]/20 transition-all duration-500"
                  />
                </div>
              )}

              {/* Dynamic Line Items Matrix */}
              <div className="mt-8 border border-slate-100 rounded-3xl p-6 bg-slate-50/50 print:p-0 print:border-none print:bg-white">
                <div className="flex justify-between items-center mb-4 print:hidden">
                  <h4 className="text-xs font-bold text-[#003087] tracking-widest uppercase">Invoice Line Items</h4>
                  <button
                    type="button"
                    onClick={() => setItems([...items, { id: Date.now().toString(), quantity: 1, unit: 'Pcs', description: '', unitPrice: 0, taxClassification: 'VATable' }])}
                    className="text-[10px] bg-[#0070BA]/10 text-[#0070BA] hover:bg-[#0070BA]/20 px-3 py-1.5 rounded-xl font-bold uppercase tracking-widest transition-all duration-500"
                  >
                    + Add Row
                  </button>
                </div>
                
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={item.id} className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-2">
                        <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1 print:text-black">Qty</label>
                        <input type="number" step="0.01" required value={item.quantity} onChange={(e) => { const newItems = [...items]; const target = newItems[index]; if (target) { target.quantity = parseFloat(e.target.value) || 0; setItems(newItems); } }} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-mono font-semibold text-[#003087] focus:border-[#0070BA] focus:ring-1 focus:ring-[#0070BA]/20 transition-all print:border-none print:p-0 print:text-black" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1 print:text-black">Unit</label>
                        <input type="text" required value={item.unit} onChange={(e) => { const newItems = [...items]; const target = newItems[index]; if (target) { target.unit = e.target.value; setItems(newItems); } }} placeholder="Pcs" className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-[#003087] focus:border-[#0070BA] focus:ring-1 focus:ring-[#0070BA]/20 transition-all print:border-none print:p-0 print:text-black" />
                      </div>
                      <div className="col-span-3">
                        <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1 print:text-black">Description</label>
                        <input type="text" required value={item.description} onChange={(e) => { const newItems = [...items]; const target = newItems[index]; if (target) { target.description = e.target.value; setItems(newItems); } }} placeholder="Item description" className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-[#003087] focus:border-[#0070BA] focus:ring-1 focus:ring-[#0070BA]/20 transition-all print:border-none print:p-0 print:text-black" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1 print:text-black">Unit Price</label>
                        <input type="number" step="0.01" required value={item.unitPrice} onChange={(e) => { const newItems = [...items]; const target = newItems[index]; if (target) { target.unitPrice = parseFloat(e.target.value) || 0; setItems(newItems); } }} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-mono font-semibold text-[#003087] focus:border-[#0070BA] focus:ring-1 focus:ring-[#0070BA]/20 transition-all print:border-none print:p-0 print:text-black" />
                      </div>
                      <div className="col-span-2 print:hidden">
                        <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Tax Class</label>
                        <select value={item.taxClassification} onChange={(e) => { const newItems = [...items]; const target = newItems[index]; if (target) { target.taxClassification = e.target.value as any; setItems(newItems); } }} className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2 text-[10px] font-semibold text-[#003087] focus:border-[#0070BA] focus:ring-1 focus:ring-[#0070BA]/20 transition-all">
                          <option value="VATable">VATable</option>
                          <option value="VAT-Exempt">VAT-Exempt</option>
                          <option value="Zero-Rated">Zero-Rated</option>
                          <option value="Senior-Citizen">SC/PWD</option>
                        </select>
                      </div>
                      <div className="col-span-1 flex justify-center print:hidden">
                        <button type="button" onClick={() => items.length > 1 && setItems(items.filter((_, i) => i !== index))} className={`p-2 rounded-xl transition-all ${items.length > 1 ? 'text-rose-500 hover:bg-rose-50' : 'text-slate-300 cursor-not-allowed'}`} disabled={items.length <= 1}>
                          <span className="text-lg leading-none">&times;</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end">
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Calculated Total Deposit</p>
                    <p className="text-2xl font-black text-[#003087] font-mono tracking-tight">₱ {finalCalculatedTotal.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* OMNICHANNEL ROUTING DROPDOWN */}
            <div className="print:hidden">
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">CHOOSE RECEIPT DELIVERY METHOD</label>
              <div className="grid grid-cols-3 gap-3">
                <button type="button" onClick={() => setDeliveryMethod('print')} className={`flex items-center justify-center gap-2 py-3 rounded-2xl text-xs font-bold uppercase tracking-widest transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${deliveryMethod === 'print' ? 'bg-[#0070BA] text-white shadow-md' : 'bg-[#F8F9FA] text-slate-500 hover:bg-slate-100 border border-slate-200'}`}>
                  <Printer className="w-4 h-4" /> Hardware Print
                </button>
                <button type="button" onClick={() => setDeliveryMethod('email')} className={`flex items-center justify-center gap-2 py-3 rounded-2xl text-xs font-bold uppercase tracking-widest transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${deliveryMethod === 'email' ? 'bg-[#0070BA] text-white shadow-md' : 'bg-[#F8F9FA] text-slate-500 hover:bg-slate-100 border border-slate-200'}`}>
                  <Mail className="w-4 h-4" /> Email Envelope
                </button>
                <button type="button" onClick={() => setDeliveryMethod('messenger')} className={`flex items-center justify-center gap-2 py-3 rounded-2xl text-xs font-bold uppercase tracking-widest transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${deliveryMethod === 'messenger' ? 'bg-[#0070BA] text-white shadow-md' : 'bg-[#F8F9FA] text-slate-500 hover:bg-slate-100 border border-slate-200'}`}>
                  <MessageCircle className="w-4 h-4" /> Messenger Ping
                </button>
              </div>
            </div>

            {/* Reconciliation Math Block */}
            <div className="bg-[#F8F9FA] rounded-3xl p-6 font-mono text-sm space-y-4 border border-slate-200 print:bg-white print:border-none print:p-2 print:border-t print:border-dashed print:rounded-none">
              <div className="flex justify-between items-center text-slate-600 print:text-black">
                <span>Gross Transaction Amount:</span>
                <span className="font-bold text-[#003087] print:text-black">₱{totalGross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-slate-600 print:text-black">
                <span>Net of VAT Sales Value:</span>
                <span className="font-bold text-[#003087] print:text-black">₱{totalNetOfVat.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-slate-600 print:text-black">
                <span>12% Output VAT Liability:</span>
                <span className="font-bold text-[#003087] print:text-black">₱{totalOutputVat.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="border-t border-slate-200 pt-4 flex justify-between items-center font-bold text-lg print:text-black print:border-black print:pt-2">
                <span className="uppercase tracking-widest">Total Accrual:</span>
                <span className="text-[#008751] print:text-black font-black">₱{finalCalculatedTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-[#003087] hover:bg-[#00205c] text-white py-4 rounded-3xl font-black tracking-widest uppercase shadow-[0_8px_30px_rgba(0,48,135,0.2)] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] flex items-center justify-center space-x-3 hover:scale-[1.02] active:scale-[0.98] print:hidden"
            >
              <CheckSquare className="w-6 h-6" />
              <span>RECORD & INITIATE DISPATCH</span>
            </button>
          </form>
        </div>

        {/* SECURE LOCAL LEDGER */}
        <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.06)] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] border border-slate-100 print:hidden flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-2xl bg-[#003087]/10 flex items-center justify-center text-[#003087] border border-[#003087]/20">
                <Database className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-black text-[#003087] tracking-tight">Cryptographic Ledger Registry</h3>
                <p className="text-[10px] text-[#0070BA] font-bold uppercase tracking-widest">Immutable IndexedDB Chain</p>
              </div>
            </div>
            
            <button onClick={handleCsvExport} className="bg-[#F8F9FA] hover:bg-slate-100 border border-slate-200 text-[#003087] px-4 py-2.5 rounded-2xl text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-sm hover:shadow-md">
              <Download className="w-4 h-4" />
              📥 I-export ang Opisyal na BIR Ledger (CSV)
            </button>
          </div>

          {ledger.length === 0 ? (
            <div className="bg-[#F8F9FA] rounded-3xl border border-slate-200 p-10 text-center flex flex-col items-center justify-center shadow-inner">
               <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm border border-slate-100">
                  <Database className="w-8 h-8 text-[#0070BA]" />
               </div>
               <h4 className="text-lg font-black text-[#003087] mb-2">Your Local Secure Ledger is Sealed and Ready</h4>
               <p className="text-sm font-semibold text-slate-500 max-w-md">Compose your first invoice above to automatically trigger the 12% EOPT accrual-basis tax engine calculation.</p>
            </div>
          ) : (
            <div className="overflow-hidden border border-slate-200 rounded-3xl bg-[#F8F9FA] shadow-inner max-h-[400px] overflow-y-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-white text-[#0070BA] font-mono text-[10px] uppercase tracking-widest sticky top-0 z-10 shadow-sm">
                    <th className="p-4 font-black">Invoice ID</th>
                    <th className="p-4 font-black">Client</th>
                    <th className="p-4 font-black">Gross Total</th>
                    <th className="p-4 font-black text-right">Hash Signature</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-mono font-semibold">
                  {ledger.map(row => (
                    <tr key={row.id} className="hover:bg-white transition-colors duration-300">
                      <td className="p-4 text-[#003087]">{row.id}</td>
                      <td className="p-4 text-slate-700">
                        {row.client} <span className="block text-[10px] text-slate-400">TIN: {row.plain_payload.tin}</span>
                      </td>
                      <td className="p-4 text-[#008751] font-black">₱{row.gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="p-4 text-right">
                        <span className="bg-[#008751]/10 text-[#008751] px-2.5 py-1 rounded-lg text-[9px] uppercase tracking-widest border border-[#008751]/20">
                          {row.encrypted_payload.block_hash.substring(0,16)}...
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </main>

      {/* TOAST NOTIFICATIONS */}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col space-y-3 pointer-events-none print:hidden">
        {toasts.map(toast => {
          let accent = "border-[#008751] bg-white text-[#008751]";
          if (toast.color === "red") accent = "border-[#D22630] bg-white text-[#D22630]";
          if (toast.color === "orange") accent = "border-amber-500 bg-white text-amber-600";
          if (toast.color === "blue") accent = "border-[#0070BA] bg-white text-[#0070BA]";

          return (
            <div key={toast.id} className={`flex flex-col p-4 rounded-3xl border shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-80 pointer-events-auto transform transition-all duration-500 animate-in slide-in-from-right-8 ease-[cubic-bezier(0.4,0,0.2,1)] ${accent}`}>
              <div className="flex items-start justify-between mb-1">
                <h4 className="text-[10px] font-black uppercase tracking-widest">{toast.title}</h4>
              </div>
              <p className="text-xs font-semibold leading-relaxed text-slate-600">{toast.body}</p>
            </div>
          );
        })}
      </div>

      <footer className="border-t border-slate-200 bg-white py-6 text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest rounded-t-3xl shadow-[0_-4px_20px_rgba(0,0,0,0.02)] mt-auto print:hidden transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
        <div className="max-w-5xl mx-auto px-4 flex flex-col items-center gap-2">
          <span>Selyo Platform Engine © 2026 | Registered under Data Privacy Act of 2012 | Terms of Service | Privacy Policy</span>
        </div>
      </footer>
    </div>
  );
}
